import {
  PortalArticleBlock,
  PortalArticleTopicBlock,
  PortalArticleTopicItem,
  PortalSummaryTopic,
} from '../../../models/PortalArtigoModel'
import { normalizePortalAnchorValue } from './anchor-utils'

export function getTopicMain(topic: PortalArticleTopicBlock) {
  return topic.items.find(
    (item): item is Extract<PortalArticleTopicItem, { type: 'section' }> =>
      item.type === 'section' && item.role === 'main'
  )
}

export function getTopicSubtopics(topic: PortalArticleTopicBlock) {
  return topic.items.filter(
    (item): item is Extract<PortalArticleTopicItem, { type: 'section' }> =>
      item.type === 'section' && item.role === 'subtopic'
  )
}

export function topicHasQuickSummary(topic: PortalArticleTopicBlock) {
  return topic.items.some((item) => item.type === 'quickSummary')
}

export function getTopicLabel(topic: PortalArticleTopicBlock) {
  return getTopicMain(topic)?.section.title.trim() || 'Tópico sem título'
}

export function moveTopicItem(
  topic: PortalArticleTopicBlock,
  fromIndex: number,
  toIndex: number
) {
  if (fromIndex === toIndex || fromIndex < 0 || toIndex < 0 || toIndex >= topic.items.length) {
    return topic
  }
  const items = [...topic.items]
  const [item] = items.splice(fromIndex, 1)
  items.splice(toIndex, 0, item)
  return { ...topic, items }
}

export function moveTopicBlock(blocks: PortalArticleBlock[], fromIndex: number, toIndex: number) {
  const next = [...blocks]
  if (fromIndex === toIndex || fromIndex < 0 || toIndex < 0 || toIndex >= next.length) return next
  if (next[fromIndex]?.type !== 'topic' || next[toIndex]?.type !== 'topic') return next
  const [topic] = next.splice(fromIndex, 1)
  next.splice(toIndex, 0, topic)
  return next
}

export function moveSubtopicBetweenTopics(
  blocks: PortalArticleBlock[],
  sourceTopicId: string,
  itemId: string | undefined,
  targetTopicId: string
) {
  if (sourceTopicId === targetTopicId || !itemId) return blocks
  const source = blocks.find(
    (block): block is PortalArticleTopicBlock => block.type === 'topic' && block.id === sourceTopicId
  )
  const target = blocks.find(
    (block): block is PortalArticleTopicBlock => block.type === 'topic' && block.id === targetTopicId
  )
  if (!source || !target) return blocks

  const item = source.items.find(
    (candidate) => candidate.type === 'section' && candidate.role === 'subtopic' && candidate.id === itemId
  )
  if (!item || item.type !== 'section') return blocks

  const nextBlocks = blocks.map((block) => {
    if (block.type !== 'topic') return block
    if (block.id === sourceTopicId) {
      return { ...block, items: block.items.filter((candidate) => candidate !== item) }
    }
    if (block.id === targetTopicId) return { ...block, items: [...block.items, item] }
    return block
  })

  return moveMatchingSummarySubtopic(nextBlocks, item.section.anchorId, target)
}

export function syncSummarySubtopicOrder(
  blocks: PortalArticleBlock[],
  topic: PortalArticleTopicBlock
) {
  const mainAnchor = normalizePortalAnchorValue(getTopicMain(topic)?.section.anchorId)
  if (!mainAnchor) return blocks
  const desiredAnchors = getTopicSubtopics(topic)
    .map((item) => normalizePortalAnchorValue(item.section.anchorId))
    .filter(Boolean)
  if (desiredAnchors.length === 0) return blocks

  return blocks.map((block) => {
    if (block.type !== 'summary') return block
    return {
      ...block,
      topics: block.topics.map((summaryTopic) => {
        if (normalizePortalAnchorValue(summaryTopic.href) !== mainAnchor) return summaryTopic
        const subtopics = summaryTopic.subtopics || []
        const byAnchor = new Map(
          subtopics.map((subtopic) => [normalizePortalAnchorValue(subtopic.href), subtopic])
        )
        const orderedMatches = desiredAnchors
          .map((anchor) => byAnchor.get(anchor))
          .filter((item): item is NonNullable<typeof item> => Boolean(item))
        let matchIndex = 0
        return {
          ...summaryTopic,
          subtopics: subtopics.map((subtopic) =>
            desiredAnchors.includes(normalizePortalAnchorValue(subtopic.href))
              ? orderedMatches[matchIndex++] || subtopic
              : subtopic
          ),
        }
      }),
    }
  })
}

function moveMatchingSummarySubtopic(
  blocks: PortalArticleBlock[],
  anchorId: string | undefined,
  targetTopic: PortalArticleTopicBlock
) {
  const anchor = normalizePortalAnchorValue(anchorId)
  const targetMainAnchor = normalizePortalAnchorValue(getTopicMain(targetTopic)?.section.anchorId)
  if (!anchor || !targetMainAnchor) return blocks

  const summary = blocks.find(
    (block): block is Extract<PortalArticleBlock, { type: 'summary' }> => block.type === 'summary'
  )
  if (!summary) return blocks

  const matches: Array<{
    topicIndex: number
    subtopicIndex: number
    value: NonNullable<PortalSummaryTopic['subtopics']>[number]
  }> = []
  summary.topics.forEach((topic, topicIndex) => {
    ;(topic.subtopics || []).forEach((subtopic, subtopicIndex) => {
      if (normalizePortalAnchorValue(subtopic.href) === anchor) {
        matches.push({ topicIndex, subtopicIndex, value: subtopic })
      }
    })
  })
  const targetSummaryIndex = summary.topics.findIndex(
    (topic) => normalizePortalAnchorValue(topic.href) === targetMainAnchor
  )
  if (matches.length !== 1 || targetSummaryIndex < 0) return blocks

  const match = matches[0]
  const topics = summary.topics.map((topic, topicIndex) => {
    const subtopics = [...(topic.subtopics || [])]
    if (topicIndex === match.topicIndex) subtopics.splice(match.subtopicIndex, 1)
    if (topicIndex === targetSummaryIndex) subtopics.push(match.value)
    return { ...topic, subtopics }
  })

  return blocks.map((block) => (block === summary ? { ...summary, topics } : block))
}
